import { gql, useQuery } from '@apollo/client';
import {
  Avatar,
  Badge,
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Spinner,
} from '@apollo/orbit';
import React from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { viewContractDetailsRoute } from 'src/app/graph/routes';
import { MarbledAvatar } from 'src/components/common/MarbledAvatar';
import { useRouteParams } from 'src/hooks/useRouteParams';
import Config from 'src/lib/config';
import {
  ContractDetailsModalQuery,
  ContractDetailsModalQueryVariables,
} from 'src/lib/graphqlTypes/types';
import { useLDFlag } from 'src/lib/launchDarkly';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { DefinitionList } from '../contractConfigWizard/contractLaunchDetailsModal/DefinitionList';

import {
  ContractDetailsHeader,
  contractDetailsHeaderFragment,
} from './ContractDetailsHeader';

const contractDetailsModalQuery = gql<
  ContractDetailsModalQuery,
  ContractDetailsModalQueryVariables
>`
  query ContractDetailsModalQuery($graphId: ID!, $variant: String!) {
    service(id: $graphId) {
      id
      avatarUrl
      title
      schemaTag(tag: $variant) {
        id
      }
      variant(name: $variant) {
        id
        name
        permissions {
          canManageBuildConfig
        }
        sourceVariant {
          id
          name
        }
        contractFilterConfig {
          exclude
          include
          hideUnreachableTypes
        }
      }
      ...ContractDetailsHeaderFragment
    }
  }
  ${contractDetailsHeaderFragment}
`;

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const ContractDetailsModal = ({ isOpen, onClose }: Props) => {
  const location = useLocation();
  const { graphId, contractVariant } = useRouteParams(viewContractDetailsRoute);
  const graphRef: GraphRef = { graphId, graphVariant: contractVariant };

  const { data, loading } = useQuery(contractDetailsModalQuery, {
    variables: {
      graphId: graphRef?.graphId,
      variant: graphRef?.graphVariant,
    },
    skip: !graphRef,
  });

  const contractConfig = data?.service?.variant?.contractFilterConfig;

  const showHideUnreachableTypes = useLDFlag(
    'mercury_show_hide_unreachable_types_toggle',
  );

  return (
    <Modal isOpen={isOpen} size="2xl" onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        {!loading && (
          <ModalHeader className="bg-secondary text-secondary">
            <ModalTitle className="flex gap-4">
              <div className="mt-2 flex">
                {data?.service?.avatarUrl ? (
                  <Avatar
                    src={data.service.avatarUrl}
                    variant="graph"
                    size="sm"
                    name={`${data.service.title} avatar`}
                  />
                ) : (
                  <MarbledAvatar
                    animate={true}
                    identifier={data?.service?.id || ''}
                  />
                )}
              </div>
              {data?.service && graphRef && (
                <ContractDetailsHeader
                  headerData={data.service}
                  graphRef={graphRef}
                />
              )}
            </ModalTitle>
          </ModalHeader>
        )}
        <ModalBody>
          {loading ? (
            <Spinner className="m-auto" />
          ) : (
            <>
              {data?.service?.variant?.permissions.canQueryBuildConfig && (
                <DefinitionList
                  title="Contract Filters"
                  list={[
                    {
                      key: 'excluded',
                      name: 'Exclude Tags',
                      value:
                        contractConfig?.exclude &&
                        contractConfig.exclude.length ? (
                          contractConfig.exclude.map((tag) => (
                            <Badge key={tag} variant="neutral">
                              {tag}
                            </Badge>
                          ))
                        ) : (
                          <Badge variant="neutral">None</Badge>
                        ),
                    },
                    {
                      key: 'included',
                      name: 'Include Tags',
                      value:
                        contractConfig?.include &&
                        contractConfig.include.length ? (
                          contractConfig.include.map((tag) => (
                            <Badge key={tag} variant="neutral">
                              {tag}
                            </Badge>
                          ))
                        ) : (
                          <Badge variant="neutral">None</Badge>
                        ),
                    },
                    {
                      key: 'hideUnreachableTypes',
                      name: 'Hide unreachable types',
                      value: (
                        <Badge variant="neutral">
                          {Boolean(
                            contractConfig?.hideUnreachableTypes,
                          ).toString()}
                        </Badge>
                      ),
                      hide: !showHideUnreachableTypes,
                    },
                  ]}
                />
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button
              variant={
                !data?.service?.variant?.permissions.canManageBuildConfig
                  ? 'primary'
                  : 'secondary'
              }
              type="button"
              onClick={onClose}
            >
              Close
            </Button>
            {data?.service?.variant?.permissions.canManageBuildConfig && (
              <Button
                as={Link}
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.editContractConfigWizard,
                  additionalSearchParameters: {
                    [Config.queryParameters.ContractVariant]: contractVariant,
                    [Config.queryParameters.SourceVariant]:
                      data.service.variant.sourceVariant?.name,
                  },
                })}
              >
                Edit Contract
              </Button>
            )}
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
